<template lang="pug">
.modern-pricing-section
  .modern-pricing-cards
    // Basic/Starter Card
    .modern-pricing-card.starter(:class="{ selected: selectedPlan === basicPlanId }")
      .current-plan-badge(v-if="currentPlanId === basicPlanId") {{ $t('current_plan') }}
      .card-wrapper(@click="$emit('select-plan', basicPlanId)")
        .card-top-section
          .top-padding-wrapper
            .top-content
              .plan-label {{ basicPlanName.toUpperCase() }}
              span.plan-discount-badge(v-if="discountLabel" :class="{ 'is-orange': selectedTime === 6 }") {{ discountLabel }}
              .price-display
                span.currency {{ feeList.currency }}
                span.original-price(v-if="selectedTime >= 6") {{ feeList[basicPlanId] }}
                span.amount {{ calcPrice(feeList[basicPlanId]) }}
                span.period /{{ $t('month') }}
        .card-content
          .upper-section
            button.plan-button {{ selectedPlan === basicPlanId ? $t('pricing.selected') : $t('pricing.choose') }}
          .features-section
            .feature-row(v-for="feature in basicFeatures" :key="feature")
              .feature-check ✓
              .feature-text {{ feature }}
        Icon(name="material-symbols:check-small-rounded" class="plan-selected-checkmark")

    // Professional/Recommended Card
    .modern-pricing-card.professional.recommended(@click="$emit('select-plan', proPlanId)" :class="{ selected: selectedPlan === proPlanId }")
      .current-plan-badge(v-if="currentPlanId === proPlanId") {{ $t('current_plan') }}
      .recommended-badge(v-if="currentPlanId !== proPlanId") {{ $t('pricing.recommended') }}
      .card-wrapper
        .card-top-section
          .top-padding-wrapper
            .top-content.gradient-top
              .plan-label {{ proPlanName.toUpperCase() }}
              span.plan-discount-badge(v-if="discountLabel" :class="{ 'is-orange': selectedTime === 6 }") {{ discountLabel }}
              .price-display
                span.currency {{ feeList.currency }}
                span.original-price(v-if="selectedTime >= 6") {{ feeList[proPlanId] }}
                span.amount {{ calcPrice(feeList[proPlanId]) }}
                span.period /{{ $t('month') }}
        .card-content
          .upper-section
            button.plan-button {{ selectedPlan === proPlanId ? $t('pricing.selected') : $t('pricing.choose') }}
          .features-section
            .feature-row(v-for="feature in proFeatures" :key="feature")
              .feature-check ✓
              .feature-text 
                NuxtLink.plan-dedicated-link(v-if="feature === $t('pricing.order_dedicated_server')" to="/dedicated-servers" @click.stop)
                  span.dedicated-link-text {{ feature }}
                  Icon(name="mdi:arrow-right-circle" class="dedicated-link-icon")
                template(v-else) {{ feature }}
        Icon(name="material-symbols:check-small-rounded" class="plan-selected-checkmark")

    // Enterprise Card
    .modern-pricing-card.enterprise
      .card-wrapper
        .card-top-section
          .top-padding-wrapper
            .top-content
              .plan-label {{ $t('pricing.enterprise') }}
              .price-display
                .custom-price {{ $t('pricing.custom') }}
        .card-content
          .upper-section
            button.plan-button(type="button" @click="goToTeams") {{ $t('header.org') }}
          .features-section
            .feature-row(v-for="feature in enterpriseFeatures" :key="feature")
              .feature-check ✓
              .feature-text
                NuxtLink.plan-dedicated-link(v-if="feature === $t('pricing.order_dedicated_server')" to="/dedicated-servers" @click.stop)
                  span.dedicated-link-text {{ feature }}
                  Icon(name="mdi:arrow-right-circle" class="dedicated-link-icon")
                template(v-else) {{ feature }}
</template>

<script lang="ts" setup>
const props = defineProps<{
  selectedCate: string
  selectedTime: number
  feeList: any
  calcPrice: (price: number) => number
  currentPlanId?: number
  modelValue?: number
}>()

const selectedPlan = computed(() => props.modelValue)

const emit = defineEmits(['select-plan', 'contact-enterprise'])

const $t = useI18n().t

// Determine plan IDs based on category
const basicPlanId = computed(() => props.selectedCate === 'home' ? 5 : 2)
const proPlanId = computed(() => props.selectedCate === 'home' ? 6 : 4)

// Plan names
const basicPlanName = computed(() => $t(`plan_names.${basicPlanId.value}`))
const proPlanName = computed(() => $t(`plan_names.${proPlanId.value}`))
const discountLabel = computed(() => {
  if (props.selectedTime === 12) return $t('pricing.20off')
  if (props.selectedTime === 6) return $t('pricing.10off')
  return ''
})

// Features based on plan type
const basicFeatures = computed(() => {
  if (props.selectedCate === 'home') {
    return [
      $t('plan_desc.plan5_quota'),
      $t('plan_desc.current_use') + ' 3 ' + $t('plan_desc.devices'),
      $t('plan_desc.all_devices_ready'),
      $t('plan_desc.anyconnect'),
      $t('plan_desc.ssltide')
    ]
  }
  // allplatform plan 2
  return [
    $t('plan_desc.plan2_quota'),
    $t('plan_desc.current_use') + ' 3 ' + $t('plan_desc.devices'),
    $t('plan_desc.all_devices_ready'),
    $t('plan_desc.provide') + ' 1Gbps ' + $t('plan_desc.china_optimized'),
    'IEPL/IPLC ' + $t('plan_desc.intl_line'),
    $t('plan_desc.ssltide'),
    $t('plan_desc.protocol_support'),
  ]
})

const proFeatures = computed(() => {
  if (props.selectedCate === 'home') {
    return [
      $t('plan_desc.plan6_quota'),
      $t('plan_desc.everything_in_basic5'),
      $t('plan_desc.current_use') + ' 5 ' + $t('plan_desc.devices'),
      $t('plan_desc.pro_only_server'),
      $t('plan_desc.new_protocol'),
      $t('pricing.priority_support'),
      $t('plan_desc.remote_support'),
      $t('pricing.advanced_analytics'),
      $t('pricing.custom_integrations'),
      $t('pricing.order_dedicated_server')
    ]
  }
  return [
    $t('plan_desc.plan4_quota'),
    $t('plan_desc.current_use') + ' 5 ' + $t('plan_desc.devices'),
    $t('plan_desc.everything_in_basic2'),
    $t('plan_desc.pro_only_server'),
    $t('plan_desc.pro_qos'),
    $t('plan_desc.new_protocol'),
    $t('pricing.priority_support'),
    $t('plan_desc.remote_support'),
    $t('pricing.advanced_analytics'),
    $t('pricing.order_dedicated_server')
  ]
})

const enterpriseFeatures = computed(() => [
  $t('pricing.custom_data_plans'),
  $t('pricing.custom_device_limits'),
  $t('pricing.order_dedicated_server'),
  $t('pricing.priority_support'),
  $t('pricing.custom_features')
])

function goToTeams() {
  return navigateTo('/orgdashboard')
}
</script>

<style lang="postcss" scoped>
.modern-pricing-section {
  --card-radius: 16px;
  --card-inset: 8px;
  --card-border: rgba(23, 23, 23, 0.08);
  --card-border-hover: rgba(23, 23, 23, 0.16);
  --card-raised: #f4f4f5;
  --card-ink: #2d5a27;
  --card-ease: cubic-bezier(0.22, 1, 0.36, 1);
  width: 100%;
  min-width: 0;
}

.dark .modern-pricing-section {
  --card-border: rgba(237, 237, 237, 0.08);
  --card-border-hover: rgba(237, 237, 237, 0.16);
  --card-raised: #19191b;
  --card-ink: var(--pricing-accent);
}

.modern-pricing-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  gap: 16px;
  width: 100%;
  min-width: 0;
}

.modern-pricing-card {
  position: relative;
  min-width: 0;
}

.modern-pricing-card.starter .card-wrapper,
.modern-pricing-card.recommended {
  cursor: pointer;
}

.modern-pricing-card .card-wrapper {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--card-border);
  border-radius: var(--card-radius);
  background: var(--pricing-solid-surface);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition:
    border-color 150ms var(--card-ease),
    box-shadow 150ms var(--card-ease);
}

.dark .modern-pricing-card .card-wrapper {
  background: #141415;
  box-shadow: none;
}

.modern-pricing-card:hover:not(.selected) .card-wrapper {
  border-color: var(--card-border-hover);
}

.modern-pricing-card.selected .card-wrapper,
.dark .modern-pricing-card.selected .card-wrapper {
  border-color: var(--pricing-accent);
  box-shadow: inset 0 0 0 1px var(--pricing-accent);
}

/* The Selected button and the accent border already mark the choice. */
.modern-pricing-card .plan-selected-checkmark {
  display: none;
}

.modern-pricing-card .top-padding-wrapper {
  padding: var(--card-inset);
}

.modern-pricing-card .top-content {
  position: relative;
  display: flex;
  min-height: 152px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 20px;
  border-radius: calc(var(--card-radius) - var(--card-inset));
  background: var(--card-raised);
}

.modern-pricing-card .plan-label {
  position: absolute;
  top: 16px;
  left: 16px;
  max-width: calc(100% - 96px);
  overflow: hidden;
  padding: 4px 8px;
  border: 1px solid var(--card-border);
  border-radius: 6px;
  color: var(--pricing-muted);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modern-pricing-card .plan-discount-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 4px 8px;
  border-radius: 6px;
  background: color-mix(in srgb, var(--pricing-accent) 14%, transparent);
  color: var(--card-ink);
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  line-height: 16px;
  white-space: nowrap;
}

.modern-pricing-card .price-display {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  color: var(--pricing-text);
  font-variant-numeric: tabular-nums;
}

.modern-pricing-card .price-display .currency {
  color: var(--pricing-muted);
  font-size: 20px;
  font-weight: 500;
  line-height: 1;
}

.modern-pricing-card .price-display .original-price {
  margin-right: 4px;
  color: var(--pricing-muted);
  font-size: 16px;
  line-height: 1;
  text-decoration: line-through;
}

.modern-pricing-card .price-display .amount {
  font-size: 48px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1;
}

.modern-pricing-card .price-display .period {
  color: var(--pricing-muted);
  font-size: 14px;
  line-height: 1;
}

.modern-pricing-card .price-display .custom-price {
  font-size: 40px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1;
}

.modern-pricing-card .card-content {
  padding: 12px 20px 20px;
}

.modern-pricing-card .upper-section {
  margin-bottom: 20px;
}

.modern-pricing-card .plan-button {
  width: 100%;
  height: 44px;
  padding: 0 16px;
  border-radius: 10px;
  background: var(--pricing-plan-button-bg);
  color: #ffffff;
  font-size: 15px;
  font-weight: 500;
  transition:
    background-color 150ms var(--card-ease),
    transform 150ms var(--card-ease);
}

.dark .modern-pricing-card .plan-button {
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  color: #ededed;
}

.modern-pricing-card .plan-button:hover {
  background: var(--pricing-plan-button-hover-bg);
}

.modern-pricing-card .plan-button:active {
  transform: scale(0.98);
}

.modern-pricing-card.selected .plan-button,
.dark .modern-pricing-card.selected .plan-button {
  background: var(--pricing-accent);
  color: var(--pricing-accent-foreground);
}

.modern-pricing-card .features-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modern-pricing-card .feature-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.modern-pricing-card .feature-check {
  display: flex;
  width: 16px;
  height: 20px;
  flex: none;
  align-items: center;
  justify-content: center;
  color: var(--pricing-muted);
  font-size: 13px;
}

.modern-pricing-card .feature-text {
  min-width: 0;
  color: var(--pricing-text);
  font-size: 14px;
  line-height: 20px;
  overflow-wrap: anywhere;
}

.modern-pricing-card .current-plan-badge,
.modern-pricing-card .recommended-badge {
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 2;
  padding: 4px 8px;
  border-radius: 6px;
  background: var(--pricing-accent);
  color: var(--pricing-accent-foreground);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  transform: translate(-50%, -50%);
  white-space: nowrap;
}

.plan-dedicated-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--card-ink);
  font-weight: 500;
  transition: color 150ms var(--card-ease);

  &:hover .dedicated-link-icon {
    transform: translateX(2px);
  }
}

.dedicated-link-icon {
  font-size: 16px;
  transition: transform 150ms var(--card-ease);
}

@media (max-width: 1120px) {
  .modern-pricing-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .modern-pricing-cards {
    gap: 12px;
  }

  .modern-pricing-card.professional {
    order: 1;
  }

  .modern-pricing-card.starter {
    order: 2;
  }

  .modern-pricing-card.enterprise {
    order: 3;
  }

  .modern-pricing-card .top-padding-wrapper {
    padding: 0;
  }

  .modern-pricing-card .top-content {
    display: grid;
    min-height: 0;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 8px;
    padding: 16px 16px 4px;
    border-radius: 0;
    background: transparent;
  }

  .modern-pricing-card .plan-label {
    position: static;
    max-width: 100%;
    padding: 0;
    border: 0;
    color: var(--pricing-text);
    font-size: 18px;
    line-height: 24px;
  }

  .modern-pricing-card .plan-discount-badge {
    position: static;
  }

  .modern-pricing-card .price-display {
    flex-wrap: nowrap;
    justify-content: flex-end;
  }

  .modern-pricing-card .price-display .currency {
    font-size: 16px;
  }

  .modern-pricing-card .price-display .original-price,
  .modern-pricing-card .price-display .period {
    font-size: 12px;
  }

  .modern-pricing-card .price-display .amount {
    font-size: 32px;
  }

  .modern-pricing-card .price-display .custom-price {
    font-size: 24px;
  }

  .modern-pricing-card .card-content {
    padding: 12px 16px 16px;
  }

  .modern-pricing-card .upper-section {
    margin-bottom: 12px;
  }

  .modern-pricing-card .plan-button {
    height: 40px;
    font-size: 14px;
  }

  .modern-pricing-card .features-section {
    gap: 8px;
  }

  .modern-pricing-card .feature-text {
    font-size: 13px;
  }

  .modern-pricing-card.professional .feature-row:nth-child(n + 7) {
    display: none;
  }
}
</style>
